import { useCallback, useEffect, useMemo, useState } from "react"
import { GetApp } from "../App"
import { GetShip } from "../App/Context"
import { Loading } from "../Components/Loading"
import { IAuditLogEdit, ICruise, ICruiseCalculations, IDay, IDayCalculations, ILog, ILogCalculations, IStatus, ITrip, ITripCalculations } from "../Types"
import { DayListType, PrintContentKind, PrintMode, PrintViewContext, PrintViewContextData, PrintViewData, PrintViewRequests, TripListType, printableDays } from "./Print/Context"
import { PrintViewProps } from "./Print/Params"
import { PrintHeaderView } from "./Print/HeaderView"
import { PrintSettingsView } from "./Print/SettingsView"
import { PrintLogbookView } from "./Print/LogbookView"
import { PrintShipView } from "./PrintShipView"
import { PrintShipViewContext, ShipDataType } from "../App/Context"
import { GetShipView, GetShipUsers } from "./ShipView"

export function PrintView(_: PrintViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const shipView = GetShipView();
  const shipUsers = GetShipUsers();
  const [data, setData] = useState<PrintViewData | undefined>(undefined);
  const [includeTripData, setIncludeTripData] = useState<boolean>(true);
  const [includeTripDayListType, setIncludeTripDayListType] = useState<TripListType>(TripListType.short);
  const [includeJournal, setIncludeJournal] = useState<boolean>(false);
  const [includeTechnical, setIncludeTechnical] = useState<boolean>(false);
  const [includeDayTrack, setIncludeDayTrack] = useState<boolean>(false);
  const [includeDayForecast, setIncludeDayForecast] = useState<boolean>(false);
  const [includeDayJournal, setIncludeDayJournal] = useState<boolean>(false);
  const [includeDayTechnical, setIncludeDayTechnical] = useState<boolean>(false);
  const [includeDayWatchplan, setIncludeDayWatchplan] = useState<boolean>(false);
  const [includeLogs, setIncludeLogs] = useState<boolean>(false);
  const [includeEdits, setIncludeEdits] = useState<boolean>(false);

  const [includeDayLogListType, setIncludeDayLogListType] = useState<DayListType>(DayListType.short);
  const [includeShipDataType, setIncludeShipDataType] = useState<ShipDataType>(ShipDataType.short);
  const [includeUserList, setIncludeUserList] = useState<boolean>(false);

  const _buildData = useCallback(() => {
  }, []);

  const load = useCallback(async () => {
    switch (shipView.printMode) {
      case PrintMode.cruise: {
        const printCruise = shipView.printRoot as ICruise;
        const cruiseId = printCruise.id;
        const cruise = await PrintViewRequests.loadCruise(app.apiClient, ship.id, cruiseId);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
        const calculation = (cruise as any).calculation;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
        let cruiseCalculations: any[] = [];
        if (calculation) {
          cruiseCalculations = [calculation];
        }

        const data: PrintViewData = {
          cruise: app.dataWithCalculations<ICruise, ICruiseCalculations>([cruise.cruise], cruiseCalculations, cruise.notes, cruise.attachments, (n) => n.cruiseId)[0],
          cruises: [],
          tracks: [],
          trips: [],
          tripLists: [],
          days: [],
          dayLists: [],
          logs: [],
          logsLists: [],
        };

        const trips = await PrintViewRequests.loadTrips(app.apiClient, ship.id, cruiseId);
        if (trips.trips) {
          data.trips = app.dataWithCalculations<ITrip, ITripCalculations>(trips.trips, trips.calculations, trips.notes, trips.attachments, (n) => n.tripId).toSorted((a, b) => a.id - b.id);
        }
        const tracks = await PrintViewRequests.loadCruiseTrack(app.apiClient, ship.id, cruiseId);
        if (tracks.tracks) {
          data.tracks = tracks.tracks;
        }

        setData(data);
        break;
      }

      case PrintMode.trips: {
        const printTrips = shipView.printRoot as ICruise;
        const tripsCruiseId = printTrips.id;
        const tripsData: PrintViewData = {
          cruise: undefined,
          cruises: [],
          tracks: [],
          trips: [],
          tripLists: [],
          days: [],
          dayLists: [],
          logs: [],
          logsLists: [],
        };

        const tripsResponse = await PrintViewRequests.loadTrips(app.apiClient, ship.id, tripsCruiseId);
        if (tripsResponse.trips) {
          const allTrips = app.dataWithCalculations<ITrip, ITripCalculations>(tripsResponse.trips, tripsResponse.calculations, tripsResponse.notes, tripsResponse.attachments, (n) => n.tripId).toSorted((a, b) => a.id - b.id);
          tripsData.trips = shipView.printTripId ? allTrips.filter((t) => t.id === shipView.printTripId) : allTrips;
        }
        const tripsTracks = await PrintViewRequests.loadCruiseTrack(app.apiClient, ship.id, tripsCruiseId);
        if (tripsTracks.tracks) {
          tripsData.tracks = tripsTracks.tracks;
        }

        let dayLists: IDay[] = [];
        for (const trip of tripsData.trips) {
          const tripDays = await PrintViewRequests.loadTripDays(app.apiClient, ship.id, trip.id);
          if (tripDays.days) {
            dayLists = [...dayLists, ...app.dataWithCalculations<IDay, IDayCalculations>(printableDays(tripDays.days), tripDays.calculations, tripDays.notes, tripDays.attachments, (n) => n.dayId)];
          }
        }
        tripsData.dayLists = dayLists.toSorted((a, b) => a.day - b.day);

        let days: IDay[] = [];
        let tripsStatuses: IStatus[] = [];
        for (const trip of tripsData.trips) {
          const tripDays = await PrintViewRequests.loadTripDays(app.apiClient, ship.id, trip.id);
          if (tripDays.days) {
            const datedDays = printableDays(tripDays.days);
            days = [...days, ...app.dataWithCalculations<IDay, IDayCalculations>(datedDays, tripDays.calculations, tripDays.notes, tripDays.attachments, (n) => n.dayId)];
            // Load statuses for each day
            for (const day of datedDays) {
              const dayLogs = await PrintViewRequests.loadDayLogs(app.apiClient, ship.id, day.id);
              if (dayLogs.statuses) {
                tripsStatuses = [...tripsStatuses, ...dayLogs.statuses];
              }
            }
          }
        }
        tripsData.days = days.toSorted((a, b) => a.day - b.day);
        tripsData.statuses = tripsStatuses;

        setData(tripsData);
        break;
      }

      case PrintMode.days: {
        const printDays = shipView.printRoot as ITrip;
        const tripId = shipView.printTripId ?? printDays.id;
        const daysData: PrintViewData = {
          cruise: undefined,
          cruises: [],
          tracks: [],
          trips: [],
          tripLists: [],
          days: [],
          dayLists: [],
          logs: [],
          logsLists: [],
        };
        const daysResponse = await PrintViewRequests.loadTripDays(app.apiClient, ship.id, tripId);
        if (daysResponse.days) {
          const allDays = app.dataWithCalculations<IDay, IDayCalculations>(printableDays(daysResponse.days), daysResponse.calculations, daysResponse.notes, daysResponse.attachments, (n) => n.dayId).toSorted((a, b) => a.day - b.day);
          daysData.days = shipView.printDayId ? allDays.filter((d) => d.id === shipView.printDayId) : allDays;
        }
        const daysTracks = await PrintViewRequests.loadTripTrack(app.apiClient, ship.id, tripId);
        if (daysTracks.tracks) {
          daysData.tracks = daysTracks.tracks;
        }

        let daysLogs: ILog[] = [];
        let daysStatuses: IStatus[] = [];
        // The day's own edit history rides the logs response (see `logs.list`), and a
        // day-edit log entry needs it to say who came aboard or left (issue #1285).
        let daysAuditLogEdits: IAuditLogEdit[] = [];
        for (const day of daysData.days) {
          const dayLogs = await PrintViewRequests.loadDayLogs(app.apiClient, ship.id, day.id);
          if (dayLogs.logs) {
            daysLogs = [...daysLogs, ...app.dataWithCalculations<ILog, ILogCalculations>(dayLogs.logs, dayLogs.calculations)];
          }
          if (dayLogs.statuses) {
            daysStatuses = [...daysStatuses, ...dayLogs.statuses];
          }
          if (dayLogs.auditLogEdits) {
            daysAuditLogEdits = [...daysAuditLogEdits, ...dayLogs.auditLogEdits];
          }
        }
        daysData.logs = daysLogs.toSorted((a, b) => a.time - b.time);
        daysData.statuses = daysStatuses;
        daysData.auditLogEdits = daysAuditLogEdits;
        setData(daysData);
        break;
      }

      case PrintMode.ship: {
        const shipAttachmentsResult = await PrintViewRequests.loadShipAttachments(app.apiClient, ship.id);
        const shipWithAttachments = ship.shipData
          ? { ...ship, shipData: { ...ship.shipData, attachments: shipAttachmentsResult.attachments || [] } }
          : ship;
        const shipData: PrintViewData = {
          cruise: undefined,
          cruises: [],
          ship: shipWithAttachments,
          tracks: [],
          trips: [],
          tripLists: [],
          days: [],
          dayLists: [],
          logs: [],
          logsLists: [],
        };
        setData(shipData);
        break;
      }
    }
  }, []);

  useEffect(() => {
    load().catch(console.error);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const content: any = useMemo(() => {
    const kind = PrintViewRequests.contentKind(shipView.printMode, data);
    if (kind === PrintContentKind.none) {
      return undefined;
    }
    if (kind === PrintContentKind.ship) {
      return <PrintShipView data={data!.ship || ship} users={shipUsers || []} />;
    }
    return <PrintLogbookView printMode={shipView.printMode} data={data} />;
  }, [data, shipUsers]);

  const context: PrintViewContextData = {
    data: data,
    includeTripData: includeTripData,
    setIncludeTripData: setIncludeTripData,
    includeTripDayListType: includeTripDayListType,
    setIncludeTripDayListType: setIncludeTripDayListType,
    includeJournal: includeJournal,
    setIncludeJournal: setIncludeJournal,
    includeTechnical: includeTechnical,
    setIncludeTechnical: setIncludeTechnical,
    includeDayTrack: includeDayTrack,
    setIncludeDayTrack: setIncludeDayTrack,
    includeDayForecast: includeDayForecast,
    setIncludeDayForecast: setIncludeDayForecast,
    includeDayJournal: includeDayJournal,
    setIncludeDayJournal: setIncludeDayJournal,
    includeDayTechnical: includeDayTechnical,
    setIncludeDayTechnical: setIncludeDayTechnical,
    includeDayWatchplan: includeDayWatchplan,
    setIncludeDayWatchplan: setIncludeDayWatchplan,
    includeLogs: includeLogs,
    setIncludeLogs: setIncludeLogs,
    includeEdits: includeEdits,
    setIncludeEdits: setIncludeEdits,
    includeDayLogListType: includeDayLogListType,
    setIncludeDayLogListType: setIncludeDayLogListType,
  };
  const contextShip = {
    includeShipData: true,
    setIncludeShipData: () => {},
    includeShipDataType: includeShipDataType,
    setIncludeShipDataType: setIncludeShipDataType,
    includeUserList: includeUserList,
    setIncludeUserList: setIncludeUserList,
    data: ship,
  };

  // Main layout of print
  return (
    <PrintViewContext.Provider value={context}>
      <PrintShipViewContext.Provider value={contextShip}>
        <div className="Print-settings no-print">
          <PrintSettingsView tripId={shipView.printTripId} />
        </div>

        <div className="print">
          <PrintHeaderView />
          {data && content ? content : <Loading />}
        </div>
      </PrintShipViewContext.Provider>
    </PrintViewContext.Provider>
  );
}

// Re-export the shared public API so existing `from "./PrintView"` imports keep working.
export { PrintMode, PrintContentKind, PrintDataOwnership, PrintViewRequests, PrintViewContext, GetPrintViewContext, TripListType, DayListType } from "./Print/Context"
export type { PrintRoot, PrintViewData, PrintViewContextData } from "./Print/Context"
